// Minimal, functional-only Lifecycle stage config UI (Module 7). No visual
// polish yet, deliberately — proves the already-tested backend (Modules 1-6)
// works end-to-end. Real design pass is a separate later task.
import React, { useEffect, useState, Suspense, lazy } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Box,
  Typography,
  List,
  IconButton,
  TextField,
  Button,
  Select,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Chip,
  Paper,
  Popover,
  Tooltip,
  Switch,
  FormControlLabel,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import AddIcon from "@mui/icons-material/Add";
import {
  fetchLifecycleStagesWithCounts,
  createLifecycleStage,
  updateLifecycleStage,
  deleteLifecycleStage,
  fetchLifecycleSettings,
  updateLifecycleSettings,
} from "../../../redux/Slice/Lifecycle/lifecycleStageSlice";

// Same lazy-loaded emoji-mart pattern already used in TextBox.jsx / TaskChatPanel.jsx
// (both existing composers) — reused here rather than adding a new picker library.
// Sentinel passed as the "stage id" when the picker is opened for the
// not-yet-created "Add Stage" row instead of an existing stage — there's no
// real _id to update against yet, so the pick is stashed in local state
// (newIcon) and sent along with the create request instead.
const NEW_STAGE_ICON_KEY = "__new__";

const EmojiPicker = lazy(() =>
  Promise.all([
    import("@emoji-mart/data"),
    import("@emoji-mart/react"),
  ]).then(([dataModule, { default: Picker }]) => ({
    default: (props) => <Picker data={dataModule.default} {...props} />,
  }))
);

function LifecycleSettings() {
  const dispatch = useDispatch();
  const theme = useTheme();
  const stages = useSelector((state) => state.lifecycleStage.stages);
  const counts = useSelector((state) => state.lifecycleStage.counts);
  const settingsActive = useSelector((state) => state.lifecycleStage.settingsActive);
  const [togglingActive, setTogglingActive] = useState(false);
  const LifecyclePermissions = useSelector(
    (s) => s.userDetails?.userPermissions?.permissions?.Lifecycle || {}
  );
  // Owner bypasses Lifecycle permission checks at the backend (roleMiddleware.js),
  // matching Tasks/TaskTemplates — same "new module" pattern (see TaskDetails.jsx's
  // isOwnerRole). The stored Lifecycle.Configure boolean defaults to false even for
  // Owner roles, so the frontend must check roleName explicitly too, or Owners get
  // wrongly blocked here despite the API letting them through.
  const roleName = useSelector((s) => s.userDetails?.userDetails?.roleName || null);
  const isOwnerRole = roleName === "Owner";
  const canConfigure = isOwnerRole || LifecyclePermissions.Configure;

  const [newName, setNewName] = useState("");
  const [newType, setNewType] = useState("active");
  const [newIcon, setNewIcon] = useState("");
  const [editingId, setEditingId] = useState(null);
  const [editingName, setEditingName] = useState("");
  const [deleteTarget, setDeleteTarget] = useState(null); // { id, affectedCount }
  const [replacementId, setReplacementId] = useState("");
  const [error, setError] = useState("");
  // Which stage's icon-picker is open, anchored to the box that was clicked.
  const [emojiAnchorEl, setEmojiAnchorEl] = useState(null);
  const [emojiStageId, setEmojiStageId] = useState(null);

  useEffect(() => {
    dispatch(fetchLifecycleStagesWithCounts());
    dispatch(fetchLifecycleSettings());
  }, [dispatch]);

  const handleToggleActive = async (event) => {
    const next = event.target.checked;
    setTogglingActive(true);
    try {
      await dispatch(updateLifecycleSettings({ active: next })).unwrap();
    } catch (err) {
      setError(typeof err === "string" ? err : "Failed to update Lifecycle status");
    } finally {
      setTogglingActive(false);
    }
  };

  if (!canConfigure) {
    return (
      <Box p={3}>
        <Alert severity="info">You don't have permission to configure Lifecycle stages.</Alert>
      </Box>
    );
  }

  const handleCreate = async () => {
    if (!newName.trim()) return;
    setError("");
    try {
      await dispatch(
        createLifecycleStage({ name: newName.trim(), stageType: newType, icon: newIcon || undefined })
      ).unwrap();
      setNewName("");
      setNewIcon("");
    } catch (err) {
      setError(typeof err === "string" ? err : "Failed to create stage");
    }
  };

  const handleRename = async (id) => {
    if (!editingName.trim()) return;
    try {
      await dispatch(updateLifecycleStage({ id, name: editingName.trim() })).unwrap();
      setEditingId(null);
    } catch (err) {
      setError(typeof err === "string" ? err : "Failed to rename stage");
    }
  };

  const attemptDelete = async (id) => {
    setError("");
    try {
      await dispatch(deleteLifecycleStage({ id })).unwrap();
    } catch (err) {
      if (err?.code === "REPLACEMENT_REQUIRED") {
        setDeleteTarget({ id, affectedCount: err.affectedCount });
      } else {
        setError(err?.message || "Failed to delete stage");
      }
    }
  };

  const confirmDeleteWithReplacement = async () => {
    if (!deleteTarget || !replacementId) return;
    try {
      await dispatch(
        deleteLifecycleStage({ id: deleteTarget.id, replacementStageId: replacementId })
      ).unwrap();
      setDeleteTarget(null);
      setReplacementId("");
    } catch (err) {
      setError(err?.message || "Failed to delete stage");
    }
  };

  const handleSetDefault = async (id) => {
    try {
      await dispatch(updateLifecycleStage({ id, isDefault: true })).unwrap();
    } catch (err) {
      setError(typeof err === "string" ? err : "Failed to set default stage");
    }
  };

  const openEmojiPicker = (event, stageId) => {
    event.stopPropagation();
    setEmojiAnchorEl(event.currentTarget);
    setEmojiStageId(stageId);
  };

  const closeEmojiPicker = () => {
    setEmojiAnchorEl(null);
    setEmojiStageId(null);
  };

  const handleEmojiSelect = async (emoji) => {
    const stageId = emojiStageId;
    closeEmojiPicker();
    if (!stageId) return;
    if (stageId === NEW_STAGE_ICON_KEY) {
      setNewIcon(emoji.native);
      return;
    }
    try {
      await dispatch(updateLifecycleStage({ id: stageId, icon: emoji.native })).unwrap();
    } catch (err) {
      setError(typeof err === "string" ? err : "Failed to update stage icon");
    }
  };

  const activeStages = stages.filter((s) => s.stageType === "active");
  const lostStages = stages.filter((s) => s.stageType === "lost");

  const renderStageRow = (stage) => (
    <Box
      key={stage._id}
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1.25,
        py: 1,
        px: 1.5,
        borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
        "&:last-of-type": { borderBottom: "none" },
      }}
    >
      <Tooltip title="Change icon" placement="top">
        <Box
          onClick={(e) => openEmojiPicker(e, stage._id)}
          sx={{
            position: "relative",
            width: 22,
            height: 22,
            borderRadius: 1,
            bgcolor: stage.color || "#64748b",
            flexShrink: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: "13px",
            lineHeight: 1,
            cursor: "pointer",
            "&:hover .lifecycle-icon-overlay": { opacity: 1 },
          }}
        >
          {stage.icon && <span>{stage.icon}</span>}
          <Box
            className="lifecycle-icon-overlay"
            sx={{
              position: "absolute",
              inset: 0,
              borderRadius: 1,
              bgcolor: "rgba(0,0,0,0.55)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              opacity: 0,
              transition: "opacity 0.12s ease",
            }}
          >
            <AddIcon sx={{ fontSize: 14, color: "#fff" }} />
          </Box>
        </Box>
      </Tooltip>
      {editingId === stage._id ? (
        <Box display="flex" gap={1} alignItems="center" flexGrow={1}>
          <TextField
            size="small"
            value={editingName}
            onChange={(e) => setEditingName(e.target.value)}
            autoFocus
          />
          <Button size="small" onClick={() => handleRename(stage._id)}>
            Save
          </Button>
          <Button size="small" onClick={() => setEditingId(null)}>
            Cancel
          </Button>
        </Box>
      ) : (
        <Box flexGrow={1} minWidth={0}>
          <Typography variant="body2" fontWeight={600} noWrap>
            {stage.name} ({counts[stage._id] || 0})
          </Typography>
        </Box>
      )}
      {stage.stageType === "active" &&
        (stage.isDefault ? (
          <Tooltip
            title={settingsActive ? "" : "Lifecycle is off — this won't be applied to new contacts right now"}
            placement="top"
          >
            <Chip
              label={settingsActive ? "Default stage" : "Default (inactive)"}
              size="small"
              sx={
                settingsActive
                  ? {
                      bgcolor: (theme) => theme.palette.primary.main,
                      color: (theme) => theme.palette.common.white,
                      fontWeight: 600,
                    }
                  : {
                      bgcolor: (theme) => theme.palette.action.disabledBackground,
                      color: (theme) => theme.palette.text.disabled,
                      fontWeight: 600,
                    }
              }
            />
          </Tooltip>
        ) : (
          <Button size="small" onClick={() => handleSetDefault(stage._id)} sx={{ whiteSpace: "nowrap" }}>
            Set as default
          </Button>
        ))}
      <IconButton
        size="small"
        onClick={() => {
          setEditingId(stage._id);
          setEditingName(stage.name);
        }}
      >
        <EditIcon fontSize="small" />
      </IconButton>
      <IconButton size="small" onClick={() => attemptDelete(stage._id)}>
        <DeleteIcon fontSize="small" />
      </IconButton>
    </Box>
  );

  return (
    <Box p={3}>
      <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
        <Typography variant="h6">Lifecycle</Typography>
        <FormControlLabel
          control={
            <Switch checked={settingsActive} onChange={handleToggleActive} disabled={togglingActive} />
          }
          label="Lifecycle active"
          labelPlacement="start"
        />
      </Box>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError("")}>
          {error}
        </Alert>
      )}
      {!settingsActive && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          Lifecycle is turned off — new contacts won't be auto-assigned a stage anymore. Existing
          contacts and manual/AI stage changes are unaffected. Turn it back on anytime.
        </Alert>
      )}

      <Box display="flex" gap={2.5} flexWrap="wrap">
        <Paper variant="outlined" sx={{ flex: "1 1 360px", borderRadius: 2, overflow: "hidden" }}>
          <Box
            sx={{
              px: 1.5,
              py: 1,
              bgcolor: (theme) => (theme.palette.mode === "dark" ? "rgba(255,255,255,0.04)" : "rgba(0,0,0,0.025)"),
            }}
          >
            <Typography variant="subtitle2" fontWeight={700}>
              Lifecycle Stages
            </Typography>
          </Box>
          <List disablePadding>{activeStages.map(renderStageRow)}</List>
        </Paper>

        <Paper variant="outlined" sx={{ flex: "1 1 300px", borderRadius: 2, overflow: "hidden" }}>
          <Box
            sx={{
              px: 1.5,
              py: 1,
              bgcolor: (theme) => (theme.palette.mode === "dark" ? "rgba(255,255,255,0.04)" : "rgba(0,0,0,0.025)"),
            }}
          >
            <Typography variant="subtitle2" fontWeight={700}>
              Lost Stages
            </Typography>
          </Box>
          <List disablePadding>{lostStages.map(renderStageRow)}</List>
        </Paper>
      </Box>

      <Box display="flex" gap={1} mt={2.5} alignItems="center">
        <Tooltip title={newIcon ? "Change icon" : "Add icon"} placement="top">
          <Box
            onClick={(e) => openEmojiPicker(e, NEW_STAGE_ICON_KEY)}
            sx={{
              position: "relative",
              width: 36,
              height: 36,
              borderRadius: 1,
              flexShrink: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: "17px",
              lineHeight: 1,
              cursor: "pointer",
              border: (theme) =>
                `1px dashed ${newIcon ? theme.palette.divider : theme.palette.text.disabled}`,
              bgcolor: (theme) =>
                theme.palette.mode === "dark" ? "rgba(255,255,255,0.04)" : "rgba(0,0,0,0.02)",
              "&:hover .lifecycle-icon-overlay": { opacity: 1 },
            }}
          >
            {newIcon || <AddIcon fontSize="small" sx={{ color: "text.disabled" }} />}
            <Box
              className="lifecycle-icon-overlay"
              sx={{
                position: "absolute",
                inset: 0,
                borderRadius: 1,
                bgcolor: "rgba(0,0,0,0.55)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                opacity: 0,
                transition: "opacity 0.12s ease",
              }}
            >
              <AddIcon sx={{ fontSize: 18, color: "#fff" }} />
            </Box>
          </Box>
        </Tooltip>
        <TextField
          size="small"
          label="New stage name"
          value={newName}
          onChange={(e) => setNewName(e.target.value)}
        />
        <Select size="small" value={newType} onChange={(e) => setNewType(e.target.value)}>
          <MenuItem value="active">Active</MenuItem>
          <MenuItem value="lost">Lost</MenuItem>
        </Select>
        <Button variant="contained" onClick={handleCreate}>
          Add Stage
        </Button>
      </Box>

      <Dialog open={!!deleteTarget} onClose={() => setDeleteTarget(null)}>
        <DialogTitle>Move contacts before deleting</DialogTitle>
        <DialogContent>
          <Typography mb={2}>
            {deleteTarget?.affectedCount} contact(s) are currently on this stage. Choose where to
            move them:
          </Typography>
          <Select
            fullWidth
            size="small"
            value={replacementId}
            onChange={(e) => setReplacementId(e.target.value)}
            displayEmpty
          >
            <MenuItem value="">
              <em>Select a stage</em>
            </MenuItem>
            {stages
              .filter((s) => s._id !== deleteTarget?.id)
              .map((s) => (
                <MenuItem key={s._id} value={s._id}>
                  {s.name}
                </MenuItem>
              ))}
          </Select>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)}>Cancel</Button>
          <Button variant="contained" disabled={!replacementId} onClick={confirmDeleteWithReplacement}>
            Move & Delete
          </Button>
        </DialogActions>
      </Dialog>

      <Popover
        open={Boolean(emojiAnchorEl)}
        anchorEl={emojiAnchorEl}
        onClose={closeEmojiPicker}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
      >
        <Suspense fallback={<Box sx={{ width: 320, height: 360 }} />}>
          <EmojiPicker onEmojiSelect={handleEmojiSelect} theme={theme.palette.mode} />
        </Suspense>
      </Popover>
    </Box>
  );
}

export default LifecycleSettings;
